import React from 'react';
import { View } from 'react-native';

import { Text } from '@/components/ui/text';
import { accountSecurityActions, type SubscriptionFlags } from '../planBilling';
import { profileWebStyles } from '../profileStyles';
import type { Translate } from '../profileTypes';
import { ProfileActionRow } from './ProfileActionRow';

interface AccountSecurityPanelProps {
  flags: SubscriptionFlags;
  /** On compact layouts these shortcuts are hidden; the plan card has them. */
  isCompact: boolean;
  onManageSubscription: () => void;
  onOpenCancelSubscription: () => void;
  onChoosePlan: () => void;
  t: Translate;
}

/**
 * "Account & security" panel: billing portal, cancel and plan shortcuts.
 * Renders nothing when no shortcut applies (always on compact layouts).
 */
export function AccountSecurityPanel({
  flags,
  isCompact,
  onManageSubscription,
  onOpenCancelSubscription,
  onChoosePlan,
  t,
}: AccountSecurityPanelProps) {
  const { isCardBackedTrial, isNoCardTrial } = flags;
  const actions = accountSecurityActions(flags, isCompact);
  if (actions.length === 0) return null;

  return (
    <View testID="profile_account_security" style={profileWebStyles.panel}>
      <Text style={profileWebStyles.panelTitle}>{t('profile.webAccountSecurity')}</Text>
      {actions.includes('billingPortal') && (
        <ProfileActionRow
          title={t('profile.webBillingPortalTitle')}
          subtitle={t('profile.webBillingPortalSubtitle')}
          icon="credit-card"
          onPress={onManageSubscription}
        />
      )}
      {actions.includes('cancel') && (
        <ProfileActionRow
          title={isCardBackedTrial
            ? t('profile.cancelTrial')
            : t('profile.webCancelSubscription')}
          subtitle={isCardBackedTrial
            ? t('profile.cancelTrialSubtitle')
            : t('profile.webCancelSubscriptionSubtitle')}
          icon="pause-circle-outline"
          onPress={onOpenCancelSubscription}
          dangerous
        />
      )}
      {actions.includes('resubscribe') && (
        <ProfileActionRow
          title={t('profile.resubscribe')}
          subtitle={t('billing.conflicts.subscriptionGoneMessage')}
          icon="workspace-premium"
          onPress={onChoosePlan}
        />
      )}
      {actions.includes('choosePlan') && (
        <ProfileActionRow
          title={t('profile.choosePlan')}
          subtitle={isNoCardTrial
            ? t('profile.choosePlanBeforeTrialEnds')
            : t('profile.choosePlanToRestoreAccess')}
          icon="workspace-premium"
          onPress={onChoosePlan}
        />
      )}
    </View>
  );
}
